/**
 * PhotoProcessingOverlay — full-screen overlay shown during capture pipeline.
 *
 * FT-343 (2026-07-08). See DESIGN-BRIEF.md §1.2.
 *
 * Contract:
 *  - `visible` is a boolean — controlled by parent.
 *  - `progress` is either null (single-photo capture) or the
 *    `{ current, total, stage }` object exposed by `usePhotoCapture`.
 *  - `onCancel` fires when the user hits the Cancel button. Parent decides
 *    whether to call `usePhotoCapture.cancel()`.
 *
 * WebKit note: no portal — the overlay renders inline via a `fixed` root, so
 * it inherits the app's stacking context. That's fine for our usage (the
 * PhotoAttachment shell is always a leaf, never inside another overlay).
 */

import React from 'react';
import { Loader2, X } from 'lucide-react';

/**
 * @param {Object} props
 * @param {boolean} props.visible
 * @param {null | { current: number, total: number, stage?: string }} [props.progress]
 * @param {() => void} [props.onCancel]
 * @param {string} [props.testId]
 */
export function PhotoProcessingOverlay({
  visible,
  progress,
  onCancel,
  testId = 'photo-processing-overlay',
}) {
  if (!visible) return null;

  const label = buildLabel(progress);

  return (
    <div
      // Semi-transparent backdrop + centered spinner
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      role="dialog"
      aria-live="polite"
      aria-label="Processing photo"
      data-testid={testId}
    >
      <div className="relative bg-white rounded-lg shadow-xl px-6 py-8 flex flex-col items-center gap-4 max-w-sm w-full mx-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            aria-label="Cancel processing"
            data-testid={`${testId}-cancel`}
            className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        )}
        <Loader2 className="h-10 w-10 text-cyan-600 animate-spin" aria-hidden />
        <p
          className="text-sm font-medium text-gray-900 text-center"
          data-testid={`${testId}-label`}
        >
          {label}
        </p>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            data-testid={`${testId}-cancel-button`}
            className="mt-2 text-sm text-gray-500 hover:text-gray-700 underline"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Human-readable label based on the progress object.
 *   - null / no total   → "Compressing photo…"
 *   - total === 1       → "Compressing photo…"
 *   - total > 1         → "Compressing (2/5)…"
 */
export function buildLabel(progress) {
  if (!progress || !progress.total || progress.total <= 1) {
    return 'Compressing photo…';
  }
  const current = Math.min(progress.current || 1, progress.total);
  return `Compressing (${current}/${progress.total})…`;
}

export default PhotoProcessingOverlay;
